import { useState, useEffect } from 'react';
import { Users, Check, Loader2 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import omnichannelService from '../../services/omnichannelService';

interface Group {
  id: number;
  name: string;
  description: string;
  agent_count: number;
  has_access: boolean;
}

interface ChannelGroupAccessConfigProps {
  channelId: string;
  channelName: string;
}

export default function ChannelGroupAccessConfig({
  channelId,
  channelName
}: ChannelGroupAccessConfigProps) {
  const { theme } = useTheme();
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    loadGroups();
  }, [channelId]);

  const loadGroups = async () => {
    try {
      setLoading(true);
      setError(null);
      const groupsData = await omnichannelService.getChannelGroups(channelId);
      setGroups(groupsData);

      // Set initially selected groups
      const selected = groupsData.filter((g: Group) => g.has_access).map((g: Group) => g.id);
      setSelectedGroupIds(selected);
    } catch (err: any) {
      console.error('Failed to load groups:', err);
      setError('Failed to load support groups. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleGroup = (groupId: number) => {
    setSelectedGroupIds((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    );
    setSuccessMessage(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      await omnichannelService.updateChannelGroupAccess(channelId, selectedGroupIds);

      setSuccessMessage(`Successfully updated group access for ${channelName}`);

      // Reload to get updated agent counts
      await loadGroups();
    } catch (err: any) {
      console.error('Failed to save group access:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const totalAgentsWithAccess = groups
    .filter((g) => selectedGroupIds.includes(g.id))
    .reduce((sum, g) => sum + g.agent_count, 0);

  if (loading) {
    return (
      <div
        className={`p-6 rounded-lg border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
      >
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`p-6 rounded-lg border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3
            className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}
          >
            Support Group Access
          </h3>
          <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Select which support groups can handle conversations from {channelName}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Users className={`h-5 w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
          <span
            className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}
          >
            {totalAgentsWithAccess} {totalAgentsWithAccess === 1 ? 'agent' : 'agents'} with access
          </span>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-3 rounded-lg bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700">
          <p className="text-sm text-green-800 dark:text-green-200">{successMessage}</p>
        </div>
      )}

      <div className="space-y-2 mb-4">
        {groups.length === 0 ? (
          <div
            className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}
          >
            <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No support groups found. Please create groups first.</p>
          </div>
        ) : (
          groups.map((group) => (
            <div
              key={group.id}
              onClick={() => handleToggleGroup(group.id)}
              className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-colors ${
                selectedGroupIds.includes(group.id)
                  ? theme === 'dark'
                    ? 'bg-blue-900/20 border-blue-700'
                    : 'bg-blue-50 border-blue-300'
                  : theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 hover:bg-gray-650'
                    : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center gap-3 flex-1">
                <div
                  className={`flex items-center justify-center w-5 h-5 rounded border-2 ${
                    selectedGroupIds.includes(group.id)
                      ? 'bg-blue-500 border-blue-500'
                      : theme === 'dark'
                        ? 'border-gray-500'
                        : 'border-gray-300'
                  }`}
                >
                  {selectedGroupIds.includes(group.id) && <Check className="h-4 w-4 text-white" />}
                </div>
                <div>
                  <h4
                    className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}
                  >
                    {group.name}
                  </h4>
                  {group.description && (
                    <p
                      className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}
                    >
                      {group.description}
                    </p>
                  )}
                </div>
              </div>
              <div
                className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}
              >
                {group.agent_count} {group.agent_count === 1 ? 'agent' : 'agents'}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="flex items-center justify-end gap-3 pt-4 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}">
        <button
          onClick={loadGroups}
          disabled={saving}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            theme === 'dark'
              ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
          } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving || groups.length === 0}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            theme === 'dark'
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-blue-500 hover:bg-blue-600 text-white'
          } ${saving || groups.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}
